import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  X, Search, Loader2, Image as ImageIcon, Save, AlertCircle,
} from 'lucide-react';
import api from '../../lib/api';
import { storiesAPI } from '../../lib/api';


// Media library picker — mirrors the one inside FeedsItemDetailPage (which is
// private to that file), so the Casted editor has the same image experience.
function MediaLibraryPicker({ open, onClose, onSelect }) {
  const [assets, setAssets]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [tags, setTags]       = useState('');
  const [offset, setOffset]   = useState(0);
  const limit = 24;

  useEffect(() => {
    if (!open) return;
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, tags, offset]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (search) params.search = search;
      if (tags)   params.tags   = tags;
      const { data } = await api.get('/media/assets', { params });
      setAssets(data.assets || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Select image from library</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setOffset(0); setSearch(e.target.value); }}
              placeholder="Search filename, caption, alt text..."
              className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500" />
          </div>
          <input value={tags} onChange={e => { setOffset(0); setTags(e.target.value); }}
            placeholder="tags (comma sep)"
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 w-44" />
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading...</div>
          ) : assets.length === 0 ? (
            <p className="text-center py-12 text-slate-400 text-sm">No images found.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {assets.map(a => (
                <button key={a.id} type="button"
                  onClick={() => onSelect({
                    file_url: a.file_url, media_asset_id: a.id,
                    caption: a.caption || '', alt_text: a.alt_text || '',
                    accreditation: a.source_attribution || '',
                  })}
                  className="group text-left border border-slate-200 rounded-lg overflow-hidden hover:border-blue-400 transition-colors">
                  <div className="aspect-video bg-slate-100">
                    <img src={a.thumbnail_url || a.file_url} alt={a.alt_text || a.filename}
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      className="w-full h-full object-cover" />
                  </div>
                  <div className="p-1.5">
                    <p className="text-[11px] text-slate-700 truncate">{a.filename}</p>
                    {typeof a.usage_count === 'number' && <p className="text-[10px] text-slate-400">Used {a.usage_count}x</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {total > limit && (
          <div className="flex items-center justify-center gap-3 px-5 py-3 border-t border-slate-200">
            <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}
              className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200">Previous</button>
            <span className="text-xs text-slate-400">Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}</span>
            <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}
              className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}


// Full-story editor for the Newsroom. Loads via GET /stories/{id}, saves via
// PUT /stories/{id}. Works for any story that writes to the stories row (used
// for casted stories, which have no feed-item editor of their own).
export default function StoryEditor({ storyId, headline: initialHeadline, onClose, onSaved }) {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [form, setForm] = useState({
    headline: '', lede: '', body: '',
    image: '', image_caption: '', image_accreditation: '',
    tagsInput: '',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await storiesAPI.getOne(storyId);
        if (!alive) return;
        setIsPublished(data.status === 'published');
        setForm({
          headline:            data.headline || '',
          lede:                data.lede || '',
          body:                data.body || '',
          image:               data.image || '',
          image_caption:       data.image_caption || '',
          image_accreditation: data.image_accreditation || '',
          tagsInput:           Array.isArray(data.tags) ? data.tags.join(', ') : '',
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load story');
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const tagsArray = form.tagsInput.split(',').map(t => t.trim()).filter(Boolean);

  const save = async () => {
    if (!form.headline.trim()) { toast.error('Headline cannot be empty'); return; }
    setSaving(true);
    try {
      await storiesAPI.update(storyId, {
        headline:            form.headline,
        lede:                form.lede,
        body:                form.body,
        image:               form.image,
        image_caption:       form.image_caption,
        image_accreditation: form.image_accreditation,
        tags:                tagsArray.map(t => t.toLowerCase()),
      });
      toast.success(isPublished ? 'Live story updated' : 'Story updated');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update story');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block';
  const inputCls = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-sm focus:outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/40" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full flex flex-col shadow-xl animate-in slide-in-from-right duration-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <h3 className="text-sm font-semibold text-slate-900 truncate pr-4">
            Edit story{initialHeadline ? ` — ${initialHeadline}` : ''}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-8"><Loader2 size={14} className="animate-spin" /> Loading story...</div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Headline</label>
                <input value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm font-semibold focus:outline-none focus:border-blue-500" />
              </div>

              <div>
                <label className={labelCls}>Lede</label>
                <textarea value={form.lede} onChange={e => setForm(f => ({ ...f, lede: e.target.value }))} rows={2}
                  className={`${inputCls} resize-none`} />
              </div>

              <div>
                <label className={labelCls}>Body</label>
                <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={14}
                  className={`${inputCls} resize-y`} />
              </div>

              <div>
                <label className={labelCls}>Image</label>
                <div className="flex items-start gap-3">
                  {form.image ? (
                    <img src={form.image} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="w-40 h-24 object-cover rounded-lg border border-slate-200 bg-slate-100 shrink-0" />
                  ) : (
                    <div className="w-40 h-24 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 shrink-0">
                      <ImageIcon size={20} />
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={() => setPickerOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-blue-400 text-slate-700 rounded text-xs font-medium transition-colors">
                      <ImageIcon size={13} /> Replace image
                    </button>
                    {form.image && (
                      <button type="button" onClick={() => setForm(f => ({ ...f, image: '' }))}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-slate-400 hover:text-red-500 rounded text-xs transition-colors">
                        <X size={13} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Image caption</label>
                  <input value={form.image_caption} onChange={e => setForm(f => ({ ...f, image_caption: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Image accreditation</label>
                  <input value={form.image_accreditation} onChange={e => setForm(f => ({ ...f, image_accreditation: e.target.value }))} className={inputCls} />
                </div>
              </div>

              <div>
                <label className={labelCls}>Tags</label>
                <input value={form.tagsInput} onChange={e => setForm(f => ({ ...f, tagsInput: e.target.value }))}
                  placeholder="comma separated, e.g. politics, harrow" className={inputCls} />
                {tagsArray.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tagsArray.map((t, i) => (
                      <span key={i} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">{t.toLowerCase()}</span>
                    ))}
                  </div>
                )}
              </div>

              {isPublished && (
                <p className="text-[11px] text-amber-600 inline-flex items-start gap-1.5">
                  <AlertCircle size={11} className="mt-0.5 shrink-0" />
                  This story is live. Saving updates the published article directly — the public site may take a few minutes to reflect changes if caching is enabled.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 shrink-0">
          <button onClick={save} disabled={saving || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-medium transition-colors">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={onClose} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-50 text-xs transition-colors">
            <X size={13} /> Cancel
          </button>
        </div>

        <MediaLibraryPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(asset) => {
            setForm(f => ({
              ...f,
              image:               asset.file_url || f.image,
              image_caption:       asset.caption || f.image_caption,
              image_accreditation: asset.accreditation || f.image_accreditation,
            }));
            setPickerOpen(false);
          }}
        />
      </div>
    </div>
  );
}